import {
    Box,
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    Link,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Alert from 'components/v4/Alert';
import AlertDescription from 'components/v4/AlertDescription';
import AlertIcon from 'components/v4/AlertIcon';
import createServiceProfile from 'API/ServiceProfiles/createServiceProfile';
import updateServiceProfiles from 'API/ServiceProfiles/updateServiceProfiles';
import useFieldValidation from 'hooks/useFieldValidation';
import { toSentenceCase } from 'utils/utils';
import LoadingIndicator from 'components/v4/LoadingIndicator';
import FormBase from '@/components/forms/new/FormBase';
import { validationSchemaTPM as validationSchema } from './schema';
import ServiceProfileVisibleScope from './formFields/ServiceProfileVisibleScope';
import { breadCrumbStyling } from 'components/forms/new/formStyles';
import useGetCurrentWholesaler from '@/queries/wholesaler/useGetCurrentWholesaler';
import { resolveWholesalerProfileName } from './serviceProfileName';

/**
 * Generic single-field ("visibleScope" only) service-profile add/edit form for
 * any "simple AppAuth" platform (RingCentral, DialPad, and any future platform
 * with the same shape — see GENERIC_SIMPLE_SERVICE_PROFILE_PLATFORMS in
 * services/platformRegistry.js). RingCentral and DialPad's previous edit forms
 * were byte-for-byte identical aside from the platform name and serviceType —
 * this replaces both.
 */
const GenericServiceProfileForm = ({
    serviceType,
    displayName,
    mode,
    data,
    handleBackButton,
    level,
    visibleScopeOptions,
}) => {
    const {
        currentWholesaler: wholesalerID,
        currentPartner: partnerID,
        companiesAndPartners,
    } = useSelector((state) => state.navigationLists);
    const { data: currentWholesalerData } = useGetCurrentWholesaler();
    const { requiredScope } = useSelector((state) => state.login);
    const { darkMode } = useSelector((state) => state.settings);

    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const defaultPayload = {
        name: resolveWholesalerProfileName(null, displayName),
        serviceType,
        partnerID: level === 'partner' ? partnerID : undefined,
        wholesalerID: level === 'partner' ? undefined : wholesalerID,
        visibleScope: level === 'partner' ? 20 : 60,
    };

    const [modalState, setModalState] = useState(data || defaultPayload);

    useEffect(() => {
        if (
            mode !== 'EDIT' &&
            level !== 'partner' &&
            currentWholesalerData?.name
        ) {
            handleChangeState({
                name: resolveWholesalerProfileName(
                    currentWholesalerData?.name,
                    displayName,
                ),
            });
        } else if (
            mode !== 'EDIT' &&
            level === 'partner' &&
            companiesAndPartners?.length > 0
        ) {
            const partnerName = companiesAndPartners.find(
                ({ id }) => id === partnerID,
            )?.name;
            handleChangeState({
                name: `${partnerName} - ${displayName} Partner Profile`,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        mode,
        companiesAndPartners,
        currentWholesalerData?.name,
        level,
        partnerID,
        wholesalerID,
    ]);

    const { validatefn } = useFieldValidation(modalState, validationSchema);

    const handleChangeState = (payload) => {
        setModalState((prev) => ({
            ...prev,
            ...payload,
        }));
    };

    if (isLoading) {
        return <LoadingIndicator />;
    }

    const initialValues = {
        name: modalState?.name || '',
        serviceType,
        partnerID:
            level === 'partner'
                ? (modalState?.partnerID ?? partnerID)
                : undefined,
        wholesalerID:
            level === 'partner'
                ? undefined
                : (modalState?.wholesalerID ?? wholesalerID),
        visibleScope:
            modalState?.visibleScope ?? (level === 'partner' ? 20 : 60),
    };

    const finalFormSubmit = async (values, actions) => {
        setIsLoading(true);
        setError(null);
        try {
            if (!validatefn(values)) {
                setError(
                    'There are some validation errors in the form. Please check and try again.',
                );
                actions.setSubmitting(false);
                setIsLoading(false);
                return;
            }
            const payload = {
                ...modalState,
                ...values,
            };
            if (mode === 'EDIT') {
                await updateServiceProfiles({
                    type: serviceType,
                    payload,
                    level,
                    scope: requiredScope,
                });
            } else {
                await createServiceProfile({
                    type: serviceType,
                    payload,
                    level,
                    scope: requiredScope,
                });
            }
            handleBackButton();
        } catch (e) {
            setError(
                e?.response?.data?.message ||
                    e?.message ||
                    'An error occurred. Please try again later.',
            );
        } finally {
            setIsLoading(false);
            actions.setSubmitting(false);
        }
    };

    const breadCrumbTrail = [
        { label: 'Services', path: ['', { index: 0 }] },
        { label: 'Configurations', path: '' },
    ];

    return (
        <>
            <Breadcrumb sx={breadCrumbStyling}>
                {breadCrumbTrail?.map((crumb, index) => (
                    <BreadcrumbItem key={index}>
                        {Array.isArray(crumb.path) ? (
                            <BreadcrumbLink
                                as={Link}
                                to={crumb.path[0]}
                                state={crumb.path[1]}
                                onClick={
                                    index === 0 ? handleBackButton : undefined
                                }>
                                {crumb.label}
                            </BreadcrumbLink>
                        ) : (
                            <BreadcrumbLink
                                as={Link}
                                to={crumb.path}
                                onClick={
                                    index === 0
                                        ? () => handleBackButton
                                        : undefined
                                }>
                                {crumb.label}
                            </BreadcrumbLink>
                        )}
                    </BreadcrumbItem>
                ))}
            </Breadcrumb>
            <FormBase
                header={`${toSentenceCase(mode)} ${modalState.name || data?.name || ''}`}
                subTitleHeader={true}
                backLink={['/service-profiles', { state: { index: 0 } }]}
                ufBackBtn="builderBackBtn"
                ufSaveBtn="builderSaveBtn"
                initialValues={initialValues}
                finalFormSubmit={finalFormSubmit}
                customClick={true}
                onBack={handleBackButton}
                isLoading={isLoading}
                enableReinitialize={true}>
                {({ errors = {}, touched = {} }) =>
                    [
                        <ServiceProfileVisibleScope
                            key={`${serviceType}ServiceProfileVisibleScope`}
                            name="visibleScope"
                            options={visibleScopeOptions}
                            darkMode={darkMode}
                            minWidth="420px"
                            maxWidth="520px"
                            width="100%"
                            errors={errors}
                            touched={touched}
                            onChange={(e) => {
                                setModalState((prev) => ({
                                    ...prev,
                                    visibleScope: e.value,
                                }));
                            }}
                        />,
                        error && (
                            <Box
                                paddingBottom="0.5rem"
                                key={`error-box-${serviceType}`}>
                                <Alert
                                    status="error"
                                    mt={4}
                                    marginBottom="1rem">
                                    <AlertIcon />
                                    <AlertDescription>{error}</AlertDescription>
                                </Alert>
                            </Box>
                        ),
                    ].filter(Boolean)
                }
            </FormBase>
        </>
    );
};

export default GenericServiceProfileForm;
